// =============================================================================
// settingsConfig.js - Settings Configuration Constants
// =============================================================================
// Centralized configuration for all Settings-related components
// =============================================================================

// =============================================================================
// TEAM CONFIGURATION
// =============================================================================
export const TEAM_CONFIG = {
  MAX_TEAM_NAME_LENGTH: 15,
  MAX_TEAMS_PER_CLUB: 30,
  DEFAULT_TEAM_COLOR: '#FF0000',
  MIN_TEAM_NAME_LENGTH: 1,
};

export const MATCH_AREA_OPTIONS = [
  'Under 6 & 7 size',
  'Quarter pitch',
  'Half Pitch',
  'Full Pitch'
];

export const DURATION_OPTIONS = [
  '40 mins',
  '50 mins',
  '60 mins',
  '70 mins',
  '80 mins',
  '90 mins'
];

// =============================================================================
// PITCH CONFIGURATION
// =============================================================================
export const PITCH_CONFIG = {
  MAX_PITCH_NAME_LENGTH: 50,
  MAX_PITCHES: 20,
  MAX_REFERENCE_POINT_LENGTH: 100,
};

export const PITCH_SECTION_OPTIONS = [
  { value: 1, label: 'Full Pitch', layout: 'FULL' },
  { value: 2, label: '2 Sections', layout: 'HALF' },
  { value: 4, label: '4 Sections', layout: 'QUARTER' },
  { value: 8, label: '8 Sections', layout: 'EIGHTH' }
];

// =============================================================================
// FACILITY CONFIGURATION (NEW)
// =============================================================================
export const FACILITY_CONFIG = {
  MAX_FACILITY_NAME_LENGTH: 100,
  MAX_ADDRESS_LENGTH: 200,
  MIN_FACILITY_NAME_LENGTH: 1,
  // Facility limits per subscription tier
  LIMITS: {
    basic: 1,
    standard: 2,
    premium: 5,
  },
};

// =============================================================================
// BRANDING CONFIGURATION
// =============================================================================
export const BRANDING_CONFIG = {
  DEFAULT_PRIMARY_COLOR: '#243665',
  DEFAULT_SECONDARY_COLOR: '#3b82f6',
  MAX_LOGO_SIZE_MB: 5,
  MAX_LOGO_SIZE_BYTES: 5 * 1024 * 1024, // 5MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'],
  MAX_SPONSOR_URL_LENGTH: 200,
};

// =============================================================================
// IMPORT/EXPORT CONFIGURATION
// =============================================================================
export const EXPORT_CONFIG = {
  CURRENT_VERSION: '2.1',
  SUPPORTED_VERSIONS: ['2.0', '2.1'],
  FILE_PREFIX: 'pitchallo-settings',
};

// =============================================================================
// FIRESTORE PATHS
// =============================================================================
export const FIRESTORE_PATHS = {
  USERS: 'users',
  CLUBS: 'clubs',
  SETTINGS: 'settings',
  GENERAL: 'general',
  SATELLITE: 'satellite',
  CONFIG: 'config',
  FACILITIES: 'facilities',           // NEW
  ALLOCATIONS: 'allocations',         // NEW
  SHARED_FACILITY_REF: 'sharedFacilityRef', // NEW
};

// Helper function to build Firestore paths
export const buildFirestorePath = {
  userDoc: (userId) => `${FIRESTORE_PATHS.USERS}/${userId}`,
  clubDoc: (clubId) => `${FIRESTORE_PATHS.CLUBS}/${clubId}`,
  clubSettings: (clubId) => `${FIRESTORE_PATHS.CLUBS}/${clubId}/${FIRESTORE_PATHS.SETTINGS}/${FIRESTORE_PATHS.GENERAL}`,
  satelliteConfig: (clubId) => `${FIRESTORE_PATHS.CLUBS}/${clubId}/${FIRESTORE_PATHS.SATELLITE}/${FIRESTORE_PATHS.CONFIG}`,
  
  // NEW: Facility paths
  facilitiesCollection: (clubId) => `${FIRESTORE_PATHS.CLUBS}/${clubId}/${FIRESTORE_PATHS.FACILITIES}`,
  facilityDoc: (clubId, facilityId) => `${FIRESTORE_PATHS.CLUBS}/${clubId}/${FIRESTORE_PATHS.FACILITIES}/${facilityId}`,
  
  // Allocations paths - organized by pitch to avoid collection limits
  // Structure: clubs/{clubId}/allocations/{pitchId}/items/{allocationId}
  // This gives us: collection=5 segments (odd), document=6 segments (even)
  allocationsCollection: (clubId, pitchId) => `${FIRESTORE_PATHS.CLUBS}/${clubId}/${FIRESTORE_PATHS.ALLOCATIONS}/${pitchId}/items`,
  allocationDoc: (clubId, pitchId, allocationId) => `${FIRESTORE_PATHS.CLUBS}/${clubId}/${FIRESTORE_PATHS.ALLOCATIONS}/${pitchId}/items/${allocationId}`,
  // Helper to get all allocations for a club (parent collection)
  allocationsParent: (clubId) => `${FIRESTORE_PATHS.CLUBS}/${clubId}/${FIRESTORE_PATHS.ALLOCATIONS}`,
  
  // NEW: Shared facility reference paths
  sharedFacilityRefCollection: (clubId) => `${FIRESTORE_PATHS.CLUBS}/${clubId}/${FIRESTORE_PATHS.SHARED_FACILITY_REF}`,
  sharedFacilityRefDoc: (clubId, refId) => `${FIRESTORE_PATHS.CLUBS}/${clubId}/${FIRESTORE_PATHS.SHARED_FACILITY_REF}/${refId}`,
};

// =============================================================================
// UI CONFIGURATION
// =============================================================================
export const UI_CONFIG = {
  MOBILE_BREAKPOINT: 768, // pixels
  SUCCESS_MESSAGE_DURATION: 3000, // milliseconds
  DEBOUNCE_DELAY: 300, // milliseconds
};

// =============================================================================
// VALIDATION MESSAGES
// =============================================================================
export const VALIDATION_MESSAGES = {
  TEAM_NAME_REQUIRED: 'Team name is required',
  TEAM_NAME_TOO_LONG: `Team name must be less than ${TEAM_CONFIG.MAX_TEAM_NAME_LENGTH} characters`,
  TEAM_NAME_DUPLICATE: 'Team name already exists',
  TEAM_LIMIT_REACHED: `Maximum ${TEAM_CONFIG.MAX_TEAMS_PER_CLUB} teams allowed per club`,
  
  PITCH_NAME_REQUIRED: 'Pitch name is required',
  PITCH_NAME_TOO_LONG: `Pitch name must be less than ${PITCH_CONFIG.MAX_PITCH_NAME_LENGTH} characters`,
  
  // NEW: Facility validation messages
  FACILITY_NAME_REQUIRED: 'Facility name is required',
  FACILITY_NAME_TOO_LONG: `Facility name must be less than ${FACILITY_CONFIG.MAX_FACILITY_NAME_LENGTH} characters`,
  FACILITY_ADDRESS_REQUIRED: 'Facility address is required',
  FACILITY_ADDRESS_TOO_LONG: `Address must be less than ${FACILITY_CONFIG.MAX_ADDRESS_LENGTH} characters`,
  FACILITY_LIMIT_REACHED: 'Maximum facilities reached for your subscription tier',
  FACILITY_HAS_ALLOCATIONS: 'Cannot delete facility with existing allocations',
  
  IMAGE_TOO_LARGE: `Image must be smaller than ${BRANDING_CONFIG.MAX_LOGO_SIZE_MB}MB`,
  IMAGE_INVALID_TYPE: 'Please upload a JPEG, PNG, or SVG image',
  
  IMPORT_INVALID_FORMAT: 'Invalid settings format',
  IMPORT_INVALID_TEAMS: 'Invalid settings format: teams array is required',
};

// =============================================================================
// COLOR PALETTE (Optional - for color picker suggestions)
// =============================================================================
export const SUGGESTED_COLORS = {
  PRIMARY: [
    '#243665', // Default dark blue
    '#1e3a8a', // Blue
    '#991b1b', // Red
    '#065f46', // Green
    '#7c2d12', // Brown
    '#4c1d95', // Purple
    '#831843', // Pink
    '#78350f', // Orange
  ],
  SECONDARY: [
    '#3b82f6', // Default light blue
    '#60a5fa', // Light blue
    '#ef4444', // Light red
    '#10b981', // Light green
    '#f59e0b', // Light orange
    '#8b5cf6', // Light purple
    '#ec4899', // Light pink
    '#f97316', // Light orange
  ],
};

// =============================================================================
// EXPORT DEFAULT CONFIG OBJECT
// =============================================================================
export default {
  TEAM_CONFIG,
  MATCH_AREA_OPTIONS,
  DURATION_OPTIONS,
  PITCH_CONFIG,
  PITCH_SECTION_OPTIONS,
  FACILITY_CONFIG,  // NEW
  BRANDING_CONFIG,
  EXPORT_CONFIG,
  FIRESTORE_PATHS,
  buildFirestorePath,
  UI_CONFIG,
  VALIDATION_MESSAGES,
  SUGGESTED_COLORS,
};
